import React from 'react';
import { FaGem } from 'react-icons/fa';

/**
 * Format date as "Day Month Year" (e.g., "2 December 2025")
 * @param {string|Date} dateValue - Date to format
 * @returns {string} Formatted date string
 */
const formatDate = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
    });
};

/**
 * GanttChart - Displays a visual timeline of project tasks, milestones, and subtasks
 * 
 * @param {Object} props - Component props
 * @param {Object} props.project - Project data with startDate, expectedCompletionDate, color, name
 * @param {Array} props.tasks - Array of task objects
 * @param {Array} props.milestones - Array of milestone objects
 * @param {Function} props.getSubtasks - Function to get subtasks for a given parent task ID
 * @param {Function} props.getTasksByMilestone - Function to group tasks by milestone
 * @param {Function} props.handleOpenTaskDetail - Callback when a task row is clicked
 */
const GanttChart = ({ 
    project, 
    tasks, 
    milestones, 
    getSubtasks, 
    getTasksByMilestone,
    handleOpenTaskDetail 
}) => {
    // Calculate date range for the Gantt chart
    const allDates = [];
    tasks.forEach(t => {
        if (t.startDate) allDates.push(new Date(t.startDate));
        if (t.expectedCompletionDate) allDates.push(new Date(t.expectedCompletionDate));
    });
    milestones.forEach(m => {
        if (m.targetDate) allDates.push(new Date(m.targetDate));
    });
    if (project.startDate) allDates.push(new Date(project.startDate));
    if (project.expectedCompletionDate) allDates.push(new Date(project.expectedCompletionDate));

    // Default to current month if no dates
    const today = new Date();
    const minDate = allDates.length > 0 
        ? new Date(Math.min(...allDates.map(d => d.getTime())))
        : new Date(today.getFullYear(), today.getMonth(), 1);
    const maxDate = allDates.length > 0 
        ? new Date(Math.max(...allDates.map(d => d.getTime())))
        : new Date(today.getFullYear(), today.getMonth() + 2, 0);

    // Add padding of 7 days on each side
    const chartStart = new Date(minDate);
    chartStart.setDate(chartStart.getDate() - 7);
    const chartEnd = new Date(maxDate);
    chartEnd.setDate(chartEnd.getDate() + 14);

    // Generate week columns
    const weeks = [];
    const currentWeek = new Date(chartStart);
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay()); // Start of week
    
    while (currentWeek <= chartEnd) {
        weeks.push(new Date(currentWeek));
        currentWeek.setDate(currentWeek.getDate() + 7);
    }

    const totalDays = Math.ceil((chartEnd - chartStart) / (1000 * 60 * 60 * 24));
    const dayWidth = Math.max(20, 1000 / totalDays); // Minimum 20px per day

    /**
     * Helper to calculate bar position and style
     * @param {string} startDate - Start date of the bar
     * @param {string} endDate - End date of the bar
     * @param {string} color - Color of the bar
     * @returns {Object|null} Style object with position and dimensions
     */
    const getBarStyle = (startDate, endDate, color = '#3498db') => {
        if (!startDate && !endDate) return null;
        const start = startDate ? new Date(startDate) : new Date(endDate);
        const end = endDate ? new Date(endDate) : new Date(startDate);
        
        const startOffset = Math.max(0, Math.floor((start - chartStart) / (1000 * 60 * 60 * 24)));
        const duration = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
        
        return {
            left: `${startOffset * dayWidth}px`,
            width: `${duration * dayWidth}px`,
            backgroundColor: color
        };
    };

    // Build hierarchical structure: Project → Milestones → Tasks → Subtasks
    const ganttRows = [];
    
    // Project row
    ganttRows.push({
        type: 'project',
        id: project.projectId,
        name: project.name,
        startDate: project.startDate,
        endDate: project.expectedCompletionDate,
        color: project.color || '#3498db',
        level: 0
    });

    // Milestones and their tasks
    const sortedMilestones = [...milestones].sort((a, b) => {
        if (!a.targetDate) return 1;
        if (!b.targetDate) return -1;
        return new Date(a.targetDate) - new Date(b.targetDate);
    });

    const tasksByMilestone = getTasksByMilestone();

    sortedMilestones.forEach(milestone => {
        // Milestone row
        ganttRows.push({
            type: 'milestone',
            id: milestone.milestoneId,
            name: milestone.name,
            startDate: null,
            endDate: milestone.targetDate,
            color: milestone.color || '#f39c12',
            level: 1,
            icon: milestone.icon || 'fa-flag'
        });

        // Tasks under this milestone
        const milestoneTasks = tasksByMilestone[milestone.milestoneId] || [];
        milestoneTasks.forEach(task => {
            ganttRows.push({
                type: 'task',
                id: task.id,
                name: task.title || task.description,
                startDate: task.startDate,
                endDate: task.expectedCompletionDate,
                color: task.status === 'Completed' || task.status === 'Closed' ? '#27ae60' : '#3498db',
                level: 2,
                status: task.status
            });

            // Subtasks
            const subtasks = getSubtasks(task.id);
            subtasks.forEach(subtask => {
                ganttRows.push({
                    type: 'subtask',
                    id: subtask.id,
                    name: subtask.title || subtask.description,
                    startDate: subtask.startDate,
                    endDate: subtask.expectedCompletionDate,
                    color: subtask.status === 'Completed' || subtask.status === 'Closed' ? '#2ecc71' : '#9b59b6',
                    level: 3,
                    status: subtask.status
                });
            });
        });
    });

    // Unassigned tasks
    const unassignedTasks = tasksByMilestone.unassigned || [];
    if (unassignedTasks.length > 0) {
        ganttRows.push({
            type: 'section',
            id: 'unassigned',
            name: 'Unassigned to Milestone',
            level: 1
        });

        unassignedTasks.forEach(task => {
            ganttRows.push({
                type: 'task',
                id: task.id,
                name: task.title || task.description,
                startDate: task.startDate,
                endDate: task.expectedCompletionDate,
                color: task.status === 'Completed' || task.status === 'Closed' ? '#27ae60' : '#95a5a6',
                level: 2,
                status: task.status
            });

            const subtasks = getSubtasks(task.id);
            subtasks.forEach(subtask => {
                ganttRows.push({
                    type: 'subtask',
                    id: subtask.id,
                    name: subtask.title || subtask.description,
                    startDate: subtask.startDate,
                    endDate: subtask.expectedCompletionDate,
                    color: subtask.status === 'Completed' || subtask.status === 'Closed' ? '#2ecc71' : '#9b59b6',
                    level: 3,
                    status: subtask.status
                });
            });
        });
    }

    return (
        <>
            {/* Date header */}
            <div className="gantt-header" style={{ width: `${totalDays * dayWidth + 300}px` }}>
                <div className="gantt-header-left">Task</div>
                <div className="gantt-header-timeline" style={{ width: `${totalDays * dayWidth}px` }}>
                    {weeks.map((week, idx) => (
                        <div key={idx} className="gantt-week-header" style={{ width: `${7 * dayWidth}px` }}>
                            {week.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </div>
                    ))}
                </div>
            </div>

            {/* Gantt rows */}
            <div className="gantt-body" style={{ width: `${totalDays * dayWidth + 300}px` }}>
                {/* Today line */}
                <div 
                    className="gantt-today-line" 
                    style={{ 
                        left: `${300 + Math.floor((today - chartStart) / (1000 * 60 * 60 * 24)) * dayWidth}px`
                    }}
                ></div>

                {ganttRows.map((row) => (
                    <div 
                        key={`${row.type}-${row.id}`} 
                        className={`gantt-row gantt-row-${row.type} level-${row.level}`}
                    >
                        <div className="gantt-row-label" style={{ paddingLeft: `${row.level * 20 + 10}px` }}>
                            {row.type === 'milestone' && (
                                <i className={`fas ${row.icon}`} style={{ color: row.color, marginRight: '6px' }}></i>
                            )}
                            <span className="row-name" onClick={() => {
                                if (row.type === 'task' || row.type === 'subtask') {
                                    const task = tasks.find(t => t.id === row.id);
                                    if (task) handleOpenTaskDetail(task);
                                }
                            }}>
                                {row.name}
                            </span>
                            {row.status && (
                                <span className={`status-badge-mini status-${row.status?.toLowerCase().replace(' ', '-')}`}>
                                    {row.status}
                                </span>
                            )}
                        </div>
                        <div className="gantt-row-timeline" style={{ width: `${totalDays * dayWidth}px` }}>
                            {row.type !== 'section' && (row.startDate || row.endDate) && (
                                <div 
                                    className={`gantt-bar gantt-bar-${row.type}`}
                                    style={getBarStyle(row.startDate, row.endDate, row.color)}
                                    title={`${row.name}\n${row.startDate ? formatDate(row.startDate) : ''} - ${row.endDate ? formatDate(row.endDate) : ''}`}
                                >
                                    {row.type === 'milestone' && (
                                        <FaGem className="milestone-marker" />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default GanttChart;
